import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Group, NativeSelect, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage, isLastOwner } from '../api/client';
import type { Invite, Member, MemberRole } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';
import { roleLabel, whenLabel } from '../format';

/** Who works on this publisher, and who has been asked to. Owners can change the list. */
export function Team() {
  const { org, user } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [members, setMembers] = useState<readonly Member[] | null>(null);
  const [invites, setInvites] = useState<readonly Invite[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setMembers(null);
    api
      .members(orgId)
      .then((body) => {
        setMembers(body.members);
        setInvites(body.invites);
      })
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  /**
   * Whether this publisher would be left with nobody who can manage it.
   *
   * Counted here rather than asked of the server, so that the change and the removal that would
   * break the rule are never offered in the first place. The API refuses them as well, and that
   * refusal is the one that matters, but a person should meet the rule as a sentence on the card
   * rather than as a failure after pressing a button.
   */
  const owners = (members ?? []).filter((member) => member.role === 'owner');

  return (
    <>
      <Title order={1} size="h2">
        Team
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Everyone here can create and publish interactions for {orgName}. Only owners can invite
        people, change what somebody can do, or take them off the team.
      </Text>

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && members === null && <Loading label="Loading your team" />}

      {members !== null && (
        <>
          <Title order={2} size="h3" mt="lg">
            People
          </Title>
          <Stack
            component="ul"
            gap="xs"
            mt="sm"
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {members.map((member) => (
              <MemberCard
                key={member.user_id}
                member={member}
                orgId={orgId ?? ''}
                orgName={orgName}
                canManage={isOwner}
                isYou={member.user_id === user?.user_id}
                onlyOwner={member.role === 'owner' && owners.length === 1}
                onChanged={load}
              />
            ))}
          </Stack>

          <Title order={2} size="h3" mt="xl">
            Pending invitations
          </Title>
          {invites.length === 0 ? (
            <Text c="dimmed" mt="sm">
              Nobody is waiting to join.
            </Text>
          ) : (
            <Stack
              component="ul"
              gap="xs"
              mt="sm"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {invites.map((invite) => (
                <PendingInvite
                  key={invite.invite_id}
                  invite={invite}
                  orgId={orgId ?? ''}
                  canCancel={isOwner}
                  onCancelled={load}
                />
              ))}
            </Stack>
          )}

          {isOwner ? (
            <InviteForm orgId={orgId ?? ''} onInvited={load} />
          ) : (
            <Text c="dimmed" mt="lg">
              Ask an owner if you need somebody added to {orgName}.
            </Text>
          )}
        </>
      )}
    </>
  );
}

/**
 * One person, what they can do here, and what an owner can do about it.
 *
 * Three things about this card are decisions rather than layout:
 *
 * - There must always be somebody who can manage the publisher, so the last owner is offered
 *   neither a role change nor a removal, and gets a sentence saying why instead. The API refuses
 *   both with `last_owner`; that refusal is the backstop for two owners working at once, not the
 *   way a person is meant to find out.
 * - Removal ends their sessions with their membership, so they are signed out wherever they are
 *   signed in. That is said in the confirmation, because it is the part a colleague would want to
 *   have been warned about.
 * - Somebody can make themselves an editor, which loses them this screen's controls, so it is
 *   confirmed in those words and the session is read again straight afterwards. They cannot remove
 *   themselves: that would sign them out on the spot, and leaving is better done by somebody who
 *   will still be here afterwards.
 */
function MemberCard({
  member,
  orgId,
  orgName,
  canManage,
  isYou,
  onlyOwner,
  onChanged,
}: {
  member: Member;
  orgId: string;
  orgName: string;
  /** True for an owner. Anybody else reads the team and is offered nothing. */
  canManage: boolean;
  isYou: boolean;
  /** True when taking this person's owner role away would leave the publisher without one. */
  onlyOwner: boolean;
  onChanged: () => void;
}) {
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [problem, setProblem] = useState<string | null>(null);
  /** The server's `last_owner` rule, said as a rule. Never a red failure card. */
  const [refused, setRefused] = useState<string | null>(null);
  /** A role that has been chosen and not yet confirmed. */
  const [pendingRole, setPendingRole] = useState<MemberRole | null>(null);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);

  const canChangeRole = canManage && !onlyOwner;
  const canRemove = canManage && !onlyOwner && !isYou;

  const refusalSentence = `${member.name} is the only owner of ${orgName} now, so this would leave nobody able to manage it. Make somebody else an owner first.`;

  async function apply(role: MemberRole): Promise<boolean> {
    setProblem(null);
    setRefused(null);
    try {
      await api.updateMember(orgId, member.user_id, role);
    } catch (error) {
      if (isLastOwner(error)) {
        setRefused(refusalSentence);
        onChanged();
        return false;
      }
      setProblem(errorMessage(error));
      return false;
    }

    if (isYou && role !== 'owner') {
      // The app is talking to an editor now, and every screen that asks what this person may do
      // has to be told before it draws anything else. Then somewhere they can still work.
      await refresh();
      notifications.show({
        message: `You are an editor of ${orgName} now.`,
        color: 'violet',
      });
      navigate('/interactions');
      return true;
    }

    notifications.show({
      message: `${member.name} is ${role === 'owner' ? 'an owner' : 'an editor'} now.`,
      color: 'violet',
    });
    onChanged();
    return true;
  }

  /**
   * Saving a role, which asks before it does anything.
   *
   * A role change takes effect on every device that person is signed in on, straight away, and
   * one of the two directions hands over the ability to remove the person making the change. So
   * the choice is made in the form and then confirmed in a sentence that says what it does.
   *
   * Returning false leaves the form open, which is what the confirmation needs: what was chosen
   * is still on screen underneath the question being asked about it.
   */
  async function saveRole(role: string): Promise<boolean> {
    const next: MemberRole = role === 'owner' ? 'owner' : 'editor';
    if (next === member.role) return true;
    setPendingRole(next);
    return false;
  }

  async function confirmRole() {
    if (pendingRole === null) return;
    setBusy(true);
    const role = pendingRole;
    setPendingRole(null);
    try {
      await apply(role);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setProblem(null);
    setRefused(null);
    setBusy(true);
    try {
      await api.removeMember(orgId, member.user_id);
      notifications.show({
        message: `${member.name} is no longer on ${orgName}.`,
        color: 'violet',
      });
      onChanged();
    } catch (error) {
      if (isLastOwner(error)) setRefused(refusalSentence);
      else setProblem(errorMessage(error));
      setBusy(false);
      setRemoving(false);
    }
  }

  return (
    <Card component="li" withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
        <div>
          <Text fw={500}>
            {member.name}
            {isYou && (
              <Text component="span" c="dimmed" size="sm">
                {' '}
                (you)
              </Text>
            )}
          </Text>

          <ReadEdit<string>
            noun={`${member.name}'s role`}
            canEdit={canChangeRole}
            open={() => member.role}
            openLabel="Change role"
            openName={`Change ${member.name}'s role`}
            saveLabel="Save role"
            saveName={`Save ${member.name}'s role`}
            busyLabel="Saving..."
            save={saveRole}
            form={(role, set) => (
              <NativeSelect
                id={`member-role-${member.user_id}`}
                label={`Role for ${member.name}`}
                description="Editors create and publish interactions. Owners can also manage the team."
                mt="xs"
                value={role}
                onChange={(event) => set(event.target.value)}
                data={[
                  { value: 'editor', label: 'Editor' },
                  { value: 'owner', label: 'Owner' },
                ]}
                style={{ maxWidth: '14rem' }}
              />
            )}
          >
            <Group gap="xs" mt={2}>
              <Text size="sm" c="dimmed">
                {member.email}
              </Text>
              <Text size="sm" c="dimmed" aria-hidden="true">
                &middot;
              </Text>
              <Text size="sm" c="dimmed">
                {roleLabel(member.role)}
              </Text>
            </Group>
          </ReadEdit>
        </div>

        {canRemove && !removing && (
          <Button
            variant="outline"
            color="red"
            size="compact-sm"
            onClick={() => setRemoving(true)}
            aria-label={`Remove ${member.name} from the team`}
          >
            Remove
          </Button>
        )}
      </Group>

      {canManage && onlyOwner && (
        <Text size="sm" c="dimmed" mt="sm">
          The only owner of {orgName}. Somebody has to be able to manage it, so make another person
          an owner before changing this role or taking {isYou ? 'yourself' : 'them'} off the team.
        </Text>
      )}

      {canManage && isYou && !onlyOwner && (
        <Text size="sm" c="dimmed" mt="sm">
          To leave {orgName}, ask another owner to take you off the team. Signing yourself out of a
          publisher is not something to do by accident.
        </Text>
      )}

      {refused !== null && (
        <Text size="sm" mt="sm">
          {refused}
        </Text>
      )}
      {problem !== null && <ErrorNote message={problem} />}

      {pendingRole !== null && (
        <Card withBorder radius="md" p="md" mt="md">
          <Text size="sm" fw={500}>
            {isYou
              ? `Make yourself an editor of ${orgName}?`
              : `Make ${member.name} ${pendingRole === 'owner' ? 'an owner' : 'an editor'}?`}
          </Text>
          <Text size="sm" mt={4}>
            {isYou
              ? 'You keep writing and publishing interactions, and you stay signed in. What you lose is everything only an owner can do: inviting people, changing the team, billing, keys and the subscriber settings. Another owner would have to give the role back.'
              : pendingRole === 'owner'
                ? `An owner can invite and remove people, including you, and can see billing, keys and every address ${orgName} holds. It takes effect straight away.`
                : `They carry on writing and publishing. What they lose, on every device they are signed in on, is the team, billing, keys and the subscriber settings.`}
          </Text>
          <Group mt="sm">
            <Button
              color="red"
              size="compact-sm"
              disabled={busy}
              onClick={() => void confirmRole()}
              autoFocus
            >
              {busy
                ? 'Saving...'
                : isYou
                  ? 'Yes, make me an editor'
                  : `Yes, make ${member.name} ${pendingRole === 'owner' ? 'an owner' : 'an editor'}`}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              disabled={busy}
              onClick={() => setPendingRole(null)}
            >
              {isYou ? 'Keep me an owner' : 'Leave it as it is'}
            </Button>
          </Group>
        </Card>
      )}

      {removing && (
        <Card withBorder radius="md" p="md" mt="md">
          <Text size="sm" fw={500}>
            Take {member.name} off {orgName}?
          </Text>
          <Text size="sm" mt={4}>
            Their sessions end the moment you press this, so they are signed out wherever they are
            signed in, including part way through something they are writing. Nothing they have made
            is deleted and readers see no change. You can invite them back, but they will have to
            accept a new invitation.
          </Text>
          <Group mt="sm">
            <Button
              color="red"
              size="compact-sm"
              disabled={busy}
              onClick={() => void remove()}
              autoFocus
            >
              {busy ? 'Removing...' : `Yes, remove ${member.name}`}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              disabled={busy}
              onClick={() => setRemoving(false)}
            >
              Keep them
            </Button>
          </Group>
        </Card>
      )}
    </Card>
  );
}

function PendingInvite({
  invite,
  orgId,
  canCancel,
  onCancelled,
}: {
  invite: Invite;
  orgId: string;
  canCancel: boolean;
  onCancelled: () => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function cancel() {
    setProblem(null);
    setBusy(true);
    try {
      await api.cancelInvite(orgId, invite.invite_id);
      onCancelled();
    } catch (error) {
      setProblem(errorMessage(error));
      setBusy(false);
    }
  }

  return (
    <Card component="li" withBorder radius="md" p="md">
      <Group justify="space-between" align="center" gap="sm">
        <div>
          <Text fw={500}>{invite.email}</Text>
          <Group gap="xs" mt={2}>
            <Text size="sm" c="dimmed">
              {roleLabel(invite.role)}
            </Text>
            <Text size="sm" c="dimmed" aria-hidden="true">
              &middot;
            </Text>
            <Text size="sm" c="dimmed">
              Expires {whenLabel(invite.expires_at)}
            </Text>
          </Group>
        </div>
        {canCancel && (
          <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            disabled={busy}
            onClick={() => void cancel()}
            aria-label={`Cancel the invitation to ${invite.email}`}
          >
            {busy ? 'Cancelling...' : 'Cancel'}
          </Button>
        )}
      </Group>
      {problem !== null && <ErrorNote message={problem} />}
    </Card>
  );
}

/** What the invitation form holds while it is being filled in. */
interface Invitation {
  readonly email: string;
  readonly role: string;
}

/**
 * Asking somebody to join, behind a press.
 *
 * An invitation is a thing you come here to make rather than a setting to look at, so the screen
 * says who is here and offers to add somebody, instead of opening with an empty box under the list.
 */
function InviteForm({ orgId, onInvited }: { orgId: string; onInvited: () => void }) {
  const [problem, setProblem] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function invite(invitation: Invitation): Promise<boolean> {
    setProblem(null);
    setSentTo(null);
    const address = invitation.email.trim();
    try {
      await api.createInvite(orgId, address, invitation.role);
      setSentTo(address);
      onInvited();
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Title order={2} size="h3">
        Invite somebody
      </Title>
      <Text size="sm" c="dimmed" mt={4}>
        We will email them a link to join. It works for seven days and can only be used once.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}
      {sentTo !== null && (
        <Text c="violet" size="sm" mt="sm">
          Invitation sent to {sentTo}.
        </Text>
      )}

      <ReadEdit<Invitation>
        noun="the invitation"
        canEdit
        asForm
        open={() => ({ email: '', role: 'editor' })}
        openLabel="Invite somebody"
        openName="Invite somebody"
        saveLabel="Send invitation"
        saveName="Send invitation"
        busyLabel="Sending..."
        save={invite}
        form={(invitation, set) => (
          <Stack gap="md" mt="md">
            <TextInput
              id="invite-email"
              label="Email address"
              type="email"
              autoComplete="off"
              required
              value={invitation.email}
              onChange={(event) => set({ ...invitation, email: event.target.value })}
              style={{ maxWidth: '22rem' }}
            />
            <NativeSelect
              id="invite-role"
              label="Role"
              description="Editors create and publish interactions. Owners can also manage the team."
              value={invitation.role}
              onChange={(event) => set({ ...invitation, role: event.target.value })}
              data={[
                { value: 'editor', label: 'Editor' },
                { value: 'owner', label: 'Owner' },
              ]}
              style={{ maxWidth: '14rem' }}
            />
          </Stack>
        )}
      />
    </Card>
  );
}
